import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormNavigation } from '../components/FormNavigation'
import { InfoBox } from '../components/InfoBox'
import { useOnboardingStore } from '../store/onboardingStore'
import { PATIENT_LANGUAGES, communicationLanguage } from '../data/patientLanguages'
import { formatDateDE } from '../utils/yousignDataHash'
import { ensureSignatureRequest } from '../utils/ensureSignatureRequest'
import { useTestMode } from '../context/TestModeContext'
import { logger } from '../utils/logger'
import { toast } from 'sonner'
import { User, MapPin, AlertTriangle } from 'lucide-react'
import { getApiErrorCode } from '../services/api'
import type { StepProps } from '../types'
import { useIsItaly } from './italy/useIsItaly'
import { ItalyConsentFields, isItalyConsentsValid } from './italy/ItalyConsentFields'
import { ITALIAN_PROVINCE_OPTIONS } from '../data/italyProvinces'

function provinceLabel(sigla: string): string {
  return ITALIAN_PROVINCE_OPTIONS.find((p) => p.value === sigla)?.label || sigla
}

function SummaryCard({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-card border border-border p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-primary">{icon}</span>
        <h3 className="text-body font-medium text-foreground">{title}</h3>
      </div>
      <div className="space-y-1.5">
        {children}
      </div>
    </div>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  if (!value) return null
  return (
    <div className="flex justify-between gap-4">
      <span className="text-small text-muted-foreground">{label}</span>
      <span className="text-small text-foreground text-right">{value}</span>
    </div>
  )
}

export function Step6DataSummary({ onNext, onBack, skipYousign }: StepProps & { skipYousign?: boolean }) {
  const { t, i18n } = useTranslation()
  const isItaly = useIsItaly()
  const {
    patientNumber,
    personalData,
    phoneNumber,
    addressData,
    italyConsents,
  } = useOnboardingStore()
  // Italy: terms + privacy acceptance is mandatory before confirming
  const consentsBlock = isItaly && !isItalyConsentsValid(italyConsents)

  const [isSubmitting, setIsSubmitting] = useState(false)
  // The signing service refused the phone number: only the patient can fix it (back to step 2)
  const [phoneRejected, setPhoneRejected] = useState(false)
  const phoneAlert = useRef<HTMLDivElement>(null)
  // On a phone the alert sits below the fold, under the sticky buttons: bring it into view
  useEffect(() => { if (phoneRejected) phoneAlert.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }) }, [phoneRejected])
  const isTestMode = useTestMode()

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Skip Yousign entirely when org has no contracts, or in test mode
      // (test runs must never create a real signature request).
      if (skipYousign || isTestMode) {
        logger.log('[Step6] Skipping Yousign', { skipYousign, isTestMode })
        onNext()
        return
      }

      const result = await ensureSignatureRequest()
      logger.log('[Step6] Signature request state:', result)

      onNext()
    } catch (error) {
      logger.error('[Step6] Yousign creation failed:', error)
      // Do NOT proceed — without a signature request the patient would hit a
      // confusing dead end at the OTP step. Say what is wrong, so a retry can work.
      const code = getApiErrorCode(error)
      if (code === 'phone_invalid') {
        setPhoneRejected(true)
        toast.error(t('common.phoneRejected'), { duration: 10000 })
      } else if (code === 'signing_temporarily_unavailable') {
        toast.error(t('common.signingUnavailable'), { duration: 12000 })
      } else if (code === 'answers_incomplete') {
        // Psychotherapy variant: a document would print a blank (the server refuses those)
        toast.error(t('pt.payment.incomplete'), { duration: 10000 })
      } else {
        toast.error(t('step6.creationError'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const genderLabel = personalData.gender ? t(`step2.${personalData.gender}`) : ''

  return (
    <div className="form-container">
      <form onSubmit={handleConfirm} className="form-inner py-12">
        <h2 className="text-h2 text-foreground mb-2">
          {t('step6.title')}
        </h2>
        {/* Italy: the name is already in the summary cards; don't repeat it here */}
        {!isItaly && (
          <p className="text-body text-foreground mb-2">
            {personalData.firstName} {personalData.lastName}
          </p>
        )}
        <p className="text-body text-muted-foreground mb-8">
          {t('step6.subtitle')}
        </p>

        <div className="space-y-4 mb-8">
          <SummaryCard icon={<User className="w-5 h-5" />} title={t('step2.title')}>
            {/* Italy: the patient number is internal — never shown to the patient */}
            {!isItaly && <SummaryRow label={t('step1.patientNumber')} value={patientNumber} />}
            <SummaryRow label={t('step2.gender')} value={genderLabel} />
            <SummaryRow label={t('step2.firstName')} value={personalData.firstName} />
            <SummaryRow label={t('step2.lastName')} value={personalData.lastName} />
            <SummaryRow label={t('step2.email')} value={personalData.email} />
            <SummaryRow label={t('step2.birthDate')} value={formatDateDE(personalData.birthDate)} />
            <SummaryRow label={t('step2.birthPlace')} value={personalData.birthPlace} />
            <SummaryRow label={t('step2.phone')} value={phoneNumber} />
            <SummaryRow label={t('step2.communicationLanguage')} value={PATIENT_LANGUAGES.find(l => l.code === communicationLanguage(personalData.communicationLanguage, i18n.language))?.label ?? ''} />
          </SummaryCard>

          <SummaryCard icon={<MapPin className="w-5 h-5" />} title={t('step3.title')}>
            <SummaryRow label={t('step3.address')} value={addressData.address} />
            {addressData.addressLine2 && (
              <SummaryRow label={t('step3.addressLine2')} value={addressData.addressLine2} />
            )}
            <SummaryRow label={t('step3.postalCode')} value={addressData.postalCode} />
            <SummaryRow label={t('step3.city')} value={addressData.city} />
            {/* Italy picks a province (region stays internal); others show state/region */}
            {isItaly && addressData.province ? (
              <SummaryRow label={t('step3.province')} value={provinceLabel(addressData.province)} />
            ) : (
              <SummaryRow label={t('step3.state')} value={addressData.state} />
            )}
            <SummaryRow label={t('step3.country')} value={addressData.country} />
          </SummaryCard>
        </div>

        {isItaly && <ItalyConsentFields />}

        <InfoBox>
          <p>{t('step6.confirmInfo')}</p>
        </InfoBox>

        {phoneRejected && (
          <div ref={phoneAlert} className="mb-6 rounded-card border border-destructive/30 bg-destructive/5 px-4 py-3 flex items-start gap-3" role="alert" data-testid="phone-rejected">
            <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-body text-foreground">{t('common.phoneRejected')}</p>
              <button type="button" onClick={() => useOnboardingStore.getState().setCurrentStep(2)} className="mt-2 text-body font-medium text-primary underline">
                {t('step6.changePhone')}
              </button>
            </div>
          </div>
        )}

        <FormNavigation
          onBack={onBack}
          nextDisabled={isSubmitting || consentsBlock}
          nextLabel={isSubmitting ? t('common.loading') : t('step6.confirmButton')}
        />
      </form>
    </div>
  )
}
